/*
 * Installing a new PWA version without closing the app.
 *
 * A downloaded version waits until every window of the old one is gone, which is why closing and
 * reopening used to be the only reliable way to update. Asking the waiting service worker to take
 * over (SKIP_WAITING, handled in the generated sw.js) and reloading once it has done so switches
 * versions immediately. The reload is driven from here rather than by workbox-window, whose own
 * reload only happens when the page already had a controller at start-up — not always the case on
 * home-screen apps after a cold start, where the button then appeared to do nothing.
 */

/** Calls `onReady` whenever a new version has finished downloading and is waiting to take over. */
export function watchForUpdates(registration: ServiceWorkerRegistration, onReady: () => void): void {
  const check = () => {
    // Without a controller this is the very first install, not an update.
    if (registration.waiting && navigator.serviceWorker.controller) {
      onReady();
    }
  };
  check();
  // workbox-window stops listening after the first update it treats as "external" (anything found
  // more than a minute after start-up), so a long-running app would never hear about a second one.
  registration.addEventListener("updatefound", () => {
    const worker = registration.installing;
    worker?.addEventListener("statechange", () => {
      if (worker.state === "installed") {
        check();
      }
    });
  });
}

/** Resolves once the version being downloaded has finished installing (or there is none). */
export function waitForInstalledWorker(
  registration: ServiceWorkerRegistration,
  timeoutMs = 60_000
): Promise<ServiceWorker | null> {
  if (registration.waiting) {
    return Promise.resolve(registration.waiting);
  }
  const installing = registration.installing;
  if (!installing) {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    const finish = () => {
      window.clearTimeout(timer);
      installing.removeEventListener("statechange", onChange);
      resolve(registration.waiting);
    };
    const onChange = () => {
      if (installing.state !== "installing") {
        finish();
      }
    };
    const timer = window.setTimeout(finish, timeoutMs);
    installing.addEventListener("statechange", onChange);
  });
}

/** Switches to the downloaded version now. With nothing waiting, the page is simply stale: reload. */
export function restartIntoUpdate(registration: ServiceWorkerRegistration | null): void {
  let reloading = false;
  const reload = () => {
    if (!reloading) {
      reloading = true;
      window.location.reload();
    }
  };
  const waiting = registration?.waiting;
  if (!waiting || !("serviceWorker" in navigator)) {
    reload();
    return;
  }
  navigator.serviceWorker.addEventListener("controllerchange", reload, { once: true });
  // Some home-screen apps miss the controller change; the new worker reaching "activated" means
  // the next load is served by it, so that is just as good a moment to reload.
  waiting.addEventListener("statechange", () => {
    if (waiting.state === "activated") {
      reload();
    }
  });
  waiting.postMessage({ type: "SKIP_WAITING" });
  window.setTimeout(reload, 10_000);
}
